// This is the admin's list of food groupings.  A food grouping gathers all the
// versions of the same item, for example every kind of tomato, so that shoppers
// see them together on the marketplace.  The admin can create, edit and delete
// them here.
import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import firebase from "../../../components/Firebase.js";
import DEFAULT_FOOD_IMAGE from "../../../images/DefaultFoodImage.js";

// The fields of a new food grouping before the admin fills it in.
const EMPTY_GROUPING = { itemName: "", category: "", displayImage: "" };

// Firestore allows 500 writes in a single batch so the food items are cleared
// in chunks that stay under that limit.
const BATCH_LIMIT = 400;

export default function FoodGroupingsTab({
  foodGroupings,
  setFoodGroupings,
  foodItems,
  setFoodItems,
}) {
  // Whether the create/edit form is open.
  const [open, setOpen] = useState(false);

  // The grouping being edited.  It is null when creating a new grouping.
  const [editingGrouping, setEditingGrouping] = useState(null);

  // The values in the create/edit form.
  const [values, setValues] = useState(EMPTY_GROUPING);

  // The grouping the admin has asked to delete, which opens the confirmation.
  const [deletingGrouping, setDeletingGrouping] = useState(null);

  // Set while the delete is running so the admin can't submit it twice.
  const [deleting, setDeleting] = useState(false);

  const database = firebase.firestore();

  const handleCreate = () => {
    setEditingGrouping(null);
    setValues(EMPTY_GROUPING);
    setOpen(true);
  };

  const handleEdit = (grouping) => {
    setEditingGrouping(grouping);
    setValues({
      itemName: grouping.itemName || "",
      category: grouping.category || "",
      displayImage: grouping.displayImage || "",
    });
    setOpen(true);
  };

  // Saves a new grouping or the changes to an existing one.
  const handleSave = (e) => {
    e.preventDefault();

    const newGrouping = {
      itemName: values.itemName.trim(),
      category: values.category.trim(),
      displayImage: values.displayImage.trim(),
    };

    // An existing grouping keeps its document so the items pointing at it stay
    // grouped.  A new grouping gets an id generated by Firestore.
    const docRef = editingGrouping
      ? database.collection("FoodGroupings").doc(editingGrouping.id)
      : database.collection("FoodGroupings").doc();

    docRef
      .set(newGrouping)
      .then(() => {
        const savedGrouping = { id: docRef.id, ...newGrouping };
        // Update the groupings hook so the list and the item forms show the
        // change right away.
        setFoodGroupings((currentGroupings) => {
          if (editingGrouping) {
            return currentGroupings.map((grouping) =>
              grouping.id === savedGrouping.id ? savedGrouping : grouping,
            );
          }
          return [...currentGroupings, savedGrouping];
        });
        setOpen(false);
        console.log("Successfully wrote to database!");
      })
      .catch((error) => {
        console.error("Error saving the food grouping: ", error);
      });
  };

  // Deletes the grouping and removes it from every food item that was using it.
  const handleDelete = async () => {
    const grouping = deletingGrouping;
    setDeleting(true);

    try {
      // Find every food item that points at this grouping.  This includes items
      // that aren't in the admin's own marketplace list.
      const foodItemDocs = await database
        .collection("FoodItems")
        .where("foodGrouping", "==", grouping.id)
        .get();

      // Clear the grouping from those items in batches so we stay within the
      // number of writes Firestore allows at once.
      for (let i = 0; i < foodItemDocs.docs.length; i += BATCH_LIMIT) {
        const batch = database.batch();
        foodItemDocs.docs.slice(i, i + BATCH_LIMIT).forEach((foodItemDoc) => {
          batch.update(foodItemDoc.ref, {
            foodGrouping: firebase.firestore.FieldValue.delete(),
          });
        });
        await batch.commit();
      }

      // Now that nothing points at the grouping it can be removed.
      await database.collection("FoodGroupings").doc(grouping.id).delete();

      // Update the hooks so the admin's list and cards match the database.
      setFoodGroupings((currentGroupings) =>
        currentGroupings.filter(
          (currentGrouping) => currentGrouping.id !== grouping.id,
        ),
      );
      setFoodItems((currentFoodItems) =>
        currentFoodItems.map((foodItem) => {
          if (foodItem.foodGrouping !== grouping.id) {
            return foodItem;
          }
          const ungroupedFoodItem = { ...foodItem };
          delete ungroupedFoodItem.foodGrouping;
          return ungroupedFoodItem;
        }),
      );
      console.log("Successfully wrote to database!");
    } catch (error) {
      console.error("Error removing the food grouping: ", error);
    }

    setDeleting(false);
    setDeletingGrouping(null);
  };

  // Counts the admin's items that are using the grouping being deleted so they
  // know what the delete will affect.
  const affectedItemCount = deletingGrouping
    ? foodItems.filter(
        (foodItem) => foodItem.foodGrouping === deletingGrouping.id,
      ).length
    : 0;

  // The groupings are listed alphabetically to make them easier to find.
  const sortedGroupings = [...foodGroupings].sort((a, b) =>
    (a.itemName || "").localeCompare(b.itemName || ""),
  );

  return (
    <div className="Page-Market">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <h2 className="Title-Marketplace">Food Groupings</h2>
        <Button
          variant="contained"
          onClick={handleCreate}
          style={{
            backgroundColor: "rgb(46, 125, 50)",
            color: "white",
          }}
        >
          CREATE A FOOD GROUPING
        </Button>
      </div>

      {sortedGroupings.length === 0 ? (
        <h3 className="EmptyMarketplace">
          No food groupings have been created yet!
        </h3>
      ) : (
        sortedGroupings.map((grouping) => (
          <div
            key={grouping.id}
            style={{
              display: "flex",
              alignItems: "center",
              background: "white",
              borderRadius: "5px",
              marginBottom: "10px",
              padding: "10px",
            }}
          >
            <img
              className="Food-Image"
              alt={grouping.itemName}
              src={grouping.displayImage || DEFAULT_FOOD_IMAGE}
              referrerPolicy="no-referrer"
              style={{
                width: "60px",
                height: "60px",
                objectFit: "cover",
                marginRight: "15px",
              }}
            />
            <div style={{ flexGrow: 1 }}>
              <p className="Item-Name-Marketplace">{grouping.itemName}</p>
              <p className="Item-Description-Marketplace">
                {grouping.category}
              </p>
            </div>
            <IconButton
              aria-label={`Edit ${grouping.itemName}`}
              onClick={() => handleEdit(grouping)}
            >
              <EditIcon />
            </IconButton>
            <IconButton
              aria-label={`Delete ${grouping.itemName}`}
              onClick={() => setDeletingGrouping(grouping)}
            >
              <DeleteIcon />
            </IconButton>
          </div>
        ))
      )}

      <Dialog
        onClose={() => setOpen(false)}
        open={open}
        aria-labelledby="food-grouping-form-title"
      >
        <DialogTitle id="food-grouping-form-title">
          {editingGrouping
            ? "Edit the food grouping"
            : "Fill in information about the food grouping"}
        </DialogTitle>
        <DialogContent>
          <form onSubmit={handleSave}>
            <TextField
              margin="dense"
              id="itemName"
              label="Grouping Name"
              name="itemName"
              value={values.itemName}
              onChange={(e) =>
                setValues({ ...values, itemName: e.target.value })
              }
              fullWidth
              required
            />
            <TextField
              margin="dense"
              id="category"
              label="Category"
              name="category"
              value={values.category}
              onChange={(e) =>
                setValues({ ...values, category: e.target.value })
              }
              fullWidth
              required
            />
            <TextField
              margin="dense"
              id="displayImage"
              label="Display Image"
              name="displayImage"
              value={values.displayImage}
              onChange={(e) =>
                setValues({ ...values, displayImage: e.target.value })
              }
              fullWidth
            />
            <DialogActions>
              <Button onClick={() => setOpen(false)} color="primary">
                Cancel
              </Button>
              <Button type="submit" color="primary">
                Save
              </Button>
            </DialogActions>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        onClose={() => setDeletingGrouping(null)}
        open={deletingGrouping !== null}
        aria-labelledby="delete-food-grouping-title"
      >
        <DialogTitle id="delete-food-grouping-title">
          Delete this food grouping?
        </DialogTitle>
        <DialogContent>
          <p>
            {deletingGrouping && deletingGrouping.itemName} will be deleted and
            removed from{" "}
            {affectedItemCount === 1 ? "1 item" : `${affectedItemCount} items`}{" "}
            that are using it. Those items will stay on the marketplace on their
            own.
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingGrouping(null)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDelete} disabled={deleting} color="secondary">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
